// Web Speech Recognition hook for FieldReport AI.
//
// Audio flow: microphone → browser SpeechRecognition → recognized text.
// No audio is saved, uploaded, or stored by FieldReport AI. The
// recognized text is inserted directly into the editor body via a
// callback. Note that the browser's Web Speech API may forward the
// captured audio to a third-party speech service (Google for
// Chromium-based browsers, Apple for Safari) — the Web editor shows a
// privacy disclosure next to the Voice button.
//
// Graceful fallback: when the browser does not support the Web Speech
// API, the hook exposes a disabled state and a user-facing reason string.
//
import { useCallback, useEffect, useRef, useState } from 'react';

export type SpeechState = 'idle' | 'listening' | 'processing' | 'error' | 'unsupported';

/** Languages supported by the in-app voice selector. */
export type SpeechLanguage = 'en-US' | 'zh-CN';

export const SPEECH_LANGUAGE_LABEL: Record<SpeechLanguage, string> = {
  'en-US': 'English',
  'zh-CN': '中文',
};

export const SPEECH_PRIVACY_NOTICE =
  "Voice recognition uses your browser's speech service. Audio may be sent to the browser provider for transcription.";

function detectDefaultLanguage(): SpeechLanguage {
  if (typeof navigator === 'undefined') return 'en-US';
  const lang = (navigator.language ?? 'en-US').toLowerCase();
  return lang.startsWith('zh') ? 'zh-CN' : 'en-US';
}

export interface UseSpeechRecognitionOptions {
  /** Called with the full accumulated transcript when the user stops speaking. */
  onResult: (text: string) => void;
  /** Called on any recognition error. */
  onError?: (message: string) => void;
  /**
   * BCP-47 language tag to use for recognition. Defaults to a value
   * derived from `navigator.language`: 'zh-CN' if it starts with 'zh',
   * otherwise 'en-US'. Callers can swap the active language at any time
   * via this option; the next `start()` call will pick it up.
   */
  language?: SpeechLanguage;
}

export interface UseSpeechRecognitionReturn {
  state: SpeechState;
  errorMessage: string | null;
  /** true when the browser does not support the Web Speech API. */
  unsupported: boolean;
  unsupportedReason: string | null;
  /** Currently active language for recognition. */
  language: SpeechLanguage;
  /** Start listening. No-op if unsupported or already listening. */
  start: () => void;
  /** Stop listening early (e.g. user clicks the button again). */
  stop: () => void;
}

/** Returns true when the viewport width is below the mobile breakpoint (880px).
 *  Re-evaluates on window resize. SSR-safe. */
export function useIsMobile(mobileBreakpoint = 880): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`).matches
      : false,
  );

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, [mobileBreakpoint]);

  return isMobile;
}

export function useSpeechRecognition(
  opts: UseSpeechRecognitionOptions,
): UseSpeechRecognitionReturn {
  const { onResult, onError, language: languageOpt } = opts;

  // PHASE 7A: language is now configurable. Default is derived from
  // navigator.language at first mount; the caller may override it on
  // subsequent renders.
  const [language, setLanguage] = useState<SpeechLanguage>(() => {
    if (languageOpt) return languageOpt;
    return detectDefaultLanguage();
  });

  // If the caller changes the language option, adopt it.
  useEffect(() => {
    if (languageOpt && languageOpt !== language) {
      setLanguage(languageOpt);
    }
  }, [languageOpt, language]);

  const [state, setState] = useState<SpeechState>(() => {
    if (typeof window === 'undefined') return 'unsupported';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sr = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    return sr ? 'idle' : 'unsupported';
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Always read the freshest language inside start() via this ref, so
  // the user can switch the selector between renders and the next
  // start() call picks it up immediately.
  const languageRef = useRef<SpeechLanguage>(language);
  languageRef.current = language;

  const recognitionRef = useRef<unknown>(null);

  const unsupported =
    typeof window === 'undefined'
      ? true
      : !(
          (window as unknown as Record<string, unknown>).SpeechRecognition ||
          (window as unknown as Record<string, unknown>).webkitSpeechRecognition
        );

  const unsupportedReason = unsupported
    ? 'Your browser does not support voice input. Please use Chrome or Edge on desktop, or Safari on iOS/macOS.'
    : null;

  const start = useCallback(() => {
    if (unsupported) return;
    if (state === 'listening') return;

    // Clean up any previous instance.
    if (recognitionRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (recognitionRef.current as any).abort();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!SR) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recognition = new SR() as any;
    recognitionRef.current = recognition;

    recognition.continuous = false;
    recognition.interimResults = true;
    // PHASE 7A: language is now configurable (en-US | zh-CN). The
    // active language is read from `languageRef` at start() so swapping
    // the selector between renders takes effect on the next session.
    recognition.lang = languageRef.current;

    let finalTranscript = '';

    recognition.onstart = () => {
      setErrorMessage(null);
      setState('listening');
    };

    recognition.onresult = (event: unknown) => {
      // SpeechRecognitionEvent — use unknown then narrow
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ev = event as any;
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
          setState('processing');
        }
      }
    };

    recognition.onend = () => {
      // Reset to idle regardless of prior state — the recognition is done.
      setState('idle');
      if (finalTranscript.trim()) {
        onResult(finalTranscript.trim());
      }
      finalTranscript = '';
    };

    recognition.onerror = (event: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ev = event as any;
      let msg = 'Voice recognition failed.';
      if (ev.error === 'not-allowed') {
        msg = 'Microphone access denied. Please allow microphone access in your browser settings.';
      } else if (ev.error === 'no-speech') {
        msg = 'No speech detected. Please try again.';
      } else if (ev.error === 'network') {
        msg = 'Network error during voice recognition. Check your connection.';
      }
      setErrorMessage(msg);
      onError?.(msg);
      setState('error');
      finalTranscript = '';
    };

    try {
      recognition.start();
    } catch {
      setErrorMessage('Could not start voice recognition.');
      setState('error');
    }
  }, [unsupported, state, onResult, onError]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (recognitionRef.current as any).stop();
    }
  }, []);

  return { state, errorMessage, unsupported, unsupportedReason, language, start, stop };
}
